/*
 * Application Insights JavaScript SDK - Channel, 2.8.16
 * Copyright (c) Microsoft and contributors. All rights reserved.
 */
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
import dynamicProto from "@microsoft/dynamicproto-js";
import { utlGetSessionStorage, utlSetSessionStorage } from "@microsoft/applicationinsights-common";
import { _throwInternal, arrForEach, arrIndexOf, dumpObj, getExceptionName, getJSON, isArray, isFunction, isString } from "@microsoft/applicationinsights-core-js";
import { _DYN_BATCH_PAYLOADS, _DYN_BUFFER_OVERRIDE, _DYN_CLEAR, _DYN_CLEAR_SENT, _DYN_COUNT, _DYN_EMIT_LINE_DELIMITED_0, _DYN_ENQUEUE, _DYN_LENGTH, _DYN_MARK_AS_SENT, _DYN_NAME_PREFIX, _DYN_PUSH, _DYN_STRINGIFY, _DYN__BUFFER__KEY, _DYN__MAX__BUFFER__SIZE, _DYN__SENT__BUFFER__KEY } from "./__DynamicConstants";
var BaseSendBuffer = /** @class */function () {
  function BaseSendBuffer(logger, config) {
    var _buffer = [];
    var _bufferFullMessageSent = false;
    this._get = function () {
      return _buffer;
    };
    this._set = function (buffer) {
      _buffer = buffer;
      return _buffer;
    };
    dynamicProto(BaseSendBuffer, this, function (_self) {
      _self[_DYN_ENQUEUE /* @min:%2eenqueue */] = function (payload) {
        if (_self[_DYN_COUNT /* @min:%2ecount */]() >= config.eventsLimitInMem()) {
          // sent internal log only once per page view
          if (!_bufferFullMessageSent) {
            _throwInternal(logger, 2 /* eLoggingSeverity.WARNING */, 105 /* _eInternalMessageId.InMemoryStorageBufferFull */, "Maximum in-memory buffer size reached: " + _self[_DYN_COUNT /* @min:%2ecount */](), true);
            _bufferFullMessageSent = true;
          }
          return;
        }
        _buffer[_DYN_PUSH /* @min:%2epush */](payload);
      };
      _self[_DYN_COUNT /* @min:%2ecount */] = function () {
        return _buffer[_DYN_LENGTH /* @min:%2elength */];
      };
      _self.size = function () {
        var size = _buffer[_DYN_LENGTH /* @min:%2elength */];
        for (var lp = 0; lp < _buffer[_DYN_LENGTH /* @min:%2elength */]; lp++) {
          size += _buffer[lp][_DYN_LENGTH /* @min:%2elength */];
        }
        if (!config[_DYN_EMIT_LINE_DELIMITED_0 /* @min:%2eemitLineDelimitedJson */]()) {
          size += 2;
        }
        return size;
      };
      _self[_DYN_CLEAR /* @min:%2eclear */] = function () {
        _buffer = [];
        _bufferFullMessageSent = false;
      };
      _self.getItems = function () {
        return _buffer.slice(0);
      };
      _self[_DYN_BATCH_PAYLOADS /* @min:%2ebatchPayloads */] = function (payload) {
        if (payload && payload[_DYN_LENGTH /* @min:%2elength */] > 0) {
          var batch = config[_DYN_EMIT_LINE_DELIMITED_0 /* @min:%2eemitLineDelimitedJson */]() ? payload.join("\n") : "[" + payload.join(",") + "]";
          return batch;
        }
        return null;
      };
    });
  }
  // Removed Stub for BaseSendBuffer.prototype.enqueue.
  // Removed Stub for BaseSendBuffer.prototype.count.
  // Removed Stub for BaseSendBuffer.prototype.size.
  // Removed Stub for BaseSendBuffer.prototype.clear.
  // Removed Stub for BaseSendBuffer.prototype.getItems.
  // Removed Stub for BaseSendBuffer.prototype.batchPayloads.
  // This is a workaround for an IE8 bug when using dynamicProto() with classes that don't have any
  // non-dynamic functions or static properties/functions when using uglify-js to minify the resulting code.
  // this will be removed when ES3 support is dropped.
  BaseSendBuffer.__ieDyn = 1;
  return BaseSendBuffer;
}();
/*
 * An array based send buffer.
 */
var ArraySendBuffer = /** @class */function (_super) {
  __extends(ArraySendBuffer, _super);
  function ArraySendBuffer(logger, config) {
    var _this = _super.call(this, logger, config) || this;
    dynamicProto(ArraySendBuffer, _this, function (_self, _base) {
      _self[_DYN_MARK_AS_SENT /* @min:%2emarkAsSent */] = function (payload) {
        _base[_DYN_CLEAR /* @min:%2eclear */]();
      };
      _self[_DYN_CLEAR_SENT /* @min:%2eclearSent */] = function (payload) {
        // not supported
      };
    });
    return _this;
  }
  // Removed Stub for ArraySendBuffer.prototype.markAsSent.
  // Removed Stub for ArraySendBuffer.prototype.clearSent.
  // This is a workaround for an IE8 bug when using dynamicProto() with classes that don't have any
  // non-dynamic functions or static properties/functions when using uglify-js to minify the resulting code.
  // this will be removed when ES3 support is dropped.
  ArraySendBuffer.__ieDyn = 1;
  return ArraySendBuffer;
}(BaseSendBuffer);
export { ArraySendBuffer };
/*
 * Session storage buffer holds a copy of all unsent items in the browser session storage.
 */
var SessionStorageSendBuffer = /** @class */function (_super) {
  __extends(SessionStorageSendBuffer, _super);
  function SessionStorageSendBuffer(logger, config) {
    var _this = _super.call(this, logger, config) || this;
    var _bufferFullMessageSent = false;
    var _a = config[_DYN_BUFFER_OVERRIDE /* @min:%2ebufferOverride */]() || {
        getItem: utlGetSessionStorage,
        setItem: utlSetSessionStorage
      },
      getItem = _a.getItem,
      setItem = _a.setItem;
    dynamicProto(SessionStorageSendBuffer, _this, function (_self, _base) {
      var bufferItems = _getBuffer(SessionStorageSendBuffer[_DYN__BUFFER__KEY /* @min:%2eBUFFER_KEY */]);
      var notDeliveredItems = _getBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */]);
      var buffer = _self._set(bufferItems.concat(notDeliveredItems));
      // If the buffer has too many items, drop items from the end.
      if (buffer[_DYN_LENGTH /* @min:%2elength */] > SessionStorageSendBuffer[_DYN__MAX__BUFFER__SIZE /* @min:%2eMAX_BUFFER_SIZE */]) {
        buffer[_DYN_LENGTH /* @min:%2elength */] = SessionStorageSendBuffer[_DYN__MAX__BUFFER__SIZE /* @min:%2eMAX_BUFFER_SIZE */];
      }
      _setBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */], []);
      _setBuffer(SessionStorageSendBuffer[_DYN__BUFFER__KEY /* @min:%2eBUFFER_KEY */], buffer);
      _self[_DYN_ENQUEUE /* @min:%2eenqueue */] = function (payload) {
        if (_self[_DYN_COUNT /* @min:%2ecount */]() >= SessionStorageSendBuffer[_DYN__MAX__BUFFER__SIZE /* @min:%2eMAX_BUFFER_SIZE */]) {
          // sent internal log only once per page view
          if (!_bufferFullMessageSent) {
            _throwInternal(logger, 2 /* eLoggingSeverity.WARNING */, 67 /* _eInternalMessageId.SessionStorageBufferFull */, "Maximum buffer size reached: " + _self[_DYN_COUNT /* @min:%2ecount */](), true);
            _bufferFullMessageSent = true;
          }
          return;
        }
        _base[_DYN_ENQUEUE /* @min:%2eenqueue */](payload);
        _setBuffer(SessionStorageSendBuffer[_DYN__BUFFER__KEY /* @min:%2eBUFFER_KEY */], _self._get());
      };
      _self[_DYN_CLEAR /* @min:%2eclear */] = function () {
        _base[_DYN_CLEAR /* @min:%2eclear */]();
        _setBuffer(SessionStorageSendBuffer[_DYN__BUFFER__KEY /* @min:%2eBUFFER_KEY */], _self._get());
        _setBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */], []);
        _bufferFullMessageSent = false;
      };
      _self[_DYN_MARK_AS_SENT /* @min:%2emarkAsSent */] = function (payload) {
        _setBuffer(SessionStorageSendBuffer[_DYN__BUFFER__KEY /* @min:%2eBUFFER_KEY */], _self._set(_removePayloadsFromBuffer(payload, _self._get())));
        var sentElements = _getBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */]);
        if (sentElements instanceof Array && payload instanceof Array) {
          sentElements = sentElements.concat(payload);
          if (sentElements[_DYN_LENGTH /* @min:%2elength */] > SessionStorageSendBuffer[_DYN__MAX__BUFFER__SIZE /* @min:%2eMAX_BUFFER_SIZE */]) {
            // We send telemetry normally. If the SENT_BUFFER is too big we don't add new elements
            // until we receive a response from the backend and the buffer has free space again (see clearSent method)
            _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 67 /* _eInternalMessageId.SessionStorageBufferFull */, "Sent buffer reached its maximum size: " + sentElements[_DYN_LENGTH /* @min:%2elength */], true);
            sentElements[_DYN_LENGTH /* @min:%2elength */] = SessionStorageSendBuffer[_DYN__MAX__BUFFER__SIZE /* @min:%2eMAX_BUFFER_SIZE */];
          }
          _setBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */], sentElements);
        }
      };
      _self[_DYN_CLEAR_SENT /* @min:%2eclearSent */] = function (payload) {
        var sentElements = _getBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */]);
        sentElements = _removePayloadsFromBuffer(payload, sentElements);
        _setBuffer(SessionStorageSendBuffer[_DYN__SENT__BUFFER__KEY /* @min:%2eSENT_BUFFER_KEY */], sentElements);
      };
      function _removePayloadsFromBuffer(payloads, buffer) {
        var remaining = [];
        arrForEach(buffer, function (value) {
          if (!isFunction(value) && arrIndexOf(payloads, value) === -1) {
            remaining[_DYN_PUSH /* @min:%2epush */](value);
          }
        });
        return remaining;
      }
      function _getBuffer(key) {
        var prefixedKey = key;
        try {
          prefixedKey = config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */] && config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */]() ? config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */]() + "_" + prefixedKey : prefixedKey;
          var bufferJson = getItem(logger, prefixedKey);
          if (bufferJson) {
            var buffer_1 = getJSON().parse(bufferJson);
            if (isString(buffer_1)) {
              // When using some version prototype.js the stringify / parse cycle does not decode array's correctly
              buffer_1 = getJSON().parse(buffer_1);
            }
            if (buffer_1 && isArray(buffer_1)) {
              return buffer_1;
            }
          }
        } catch (e) {
          _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 42 /* _eInternalMessageId.FailedToRestoreStorageBuffer */, " storage key: " + prefixedKey + ", " + getExceptionName(e), {
            exception: dumpObj(e)
          });
        }
        return [];
      }
      function _setBuffer(key, buffer) {
        var prefixedKey = key;
        try {
          prefixedKey = config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */] && config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */]() ? config[_DYN_NAME_PREFIX /* @min:%2enamePrefix */]() + "_" + prefixedKey : prefixedKey;
          var bufferJson = JSON[_DYN_STRINGIFY /* @min:%2estringify */](buffer);
          setItem(logger, prefixedKey, bufferJson);
        } catch (e) {
          // if there was an error, clear the buffer
          // telemetry is stored in the _buffer array so we won't loose any items
          setItem(logger, prefixedKey, JSON[_DYN_STRINGIFY /* @min:%2estringify */]([]));
          _throwInternal(logger, 2 /* eLoggingSeverity.WARNING */, 41 /* _eInternalMessageId.FailedToSetStorageBuffer */, " storage key: " + prefixedKey + ", " + getExceptionName(e) + ". Buffer cleared", {
            exception: dumpObj(e)
          });
        }
      }
    });
    return _this;
  }
  // Removed Stub for SessionStorageSendBuffer.prototype.enqueue.
  // Removed Stub for SessionStorageSendBuffer.prototype.clear.
  // Removed Stub for SessionStorageSendBuffer.prototype.markAsSent.
  // Removed Stub for SessionStorageSendBuffer.prototype.clearSent.
  SessionStorageSendBuffer.BUFFER_KEY = "AI_buffer";
  SessionStorageSendBuffer.SENT_BUFFER_KEY = "AI_sentBuffer";
  // Maximum number of payloads stored in the buffer. If the buffer is full, new elements will be dropped.
  SessionStorageSendBuffer.MAX_BUFFER_SIZE = 2000;
  return SessionStorageSendBuffer;
}(BaseSendBuffer);
export { SessionStorageSendBuffer };
//# sourceMappingURL=SendBuffer.js.map